/**
 * Public pricing card data for /pricing.
 * Keep in sync with: src/lib/billing/limits.ts and docs/founding/pricing/wisprs_pricing_tables.md
 * See docs/SOURCE_OF_TRUTH.md.
 *
 * Free-tier numbers are IMPORTED, not retyped — see FREE_* below. This file used
 * to hardcode "STT: 30 minutes / day" next to an undisclosed per-file cap the
 * page never mentioned, which is how users arrived with 45-142 minute files and
 * were rejected on their first upload.
 */
import {
  FREE_MINUTES_PER_DAY,
  FREE_MAX_FILE_MINUTES,
  FREE_WELCOME_FILE_MAX_MINUTES,
} from './billing/limits';

export interface Plan {
  id: 'free' | 'pro' | 'studio' | 'agency' | 'enterprise';
  name: string;
  monthlyPrice: number;
  annualPrice?: number;
  sttMinutes: number;
  ttsCharacters: number;
  tagline: string;
  features: string[];
  premiumVoiceCap?: number; // Percentage cap (10, 25, 35)
  cta: string;
  popular?: boolean;
  isCustomPricing?: boolean; // For Enterprise - indicates custom pricing
  customPricingLabel?: string; // Custom label for pricing display
}

export const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    sttMinutes: 30,
    ttsCharacters: 5000,
    tagline: 'Get started at no cost',
    features: [
      // Derived from PLAN_LIMITS + the welcome-file env ceiling so this copy can
      // never drift from what the gate actually enforces (it did: the page said
      // "30 minutes / day" while a separate, undisclosed 30-min PER FILE cap
      // silently rejected every long upload).
      `Your first file free, any length up to ${FREE_WELCOME_FILE_MAX_MINUTES / 60} hours`,
      `Then ${FREE_MINUTES_PER_DAY} minutes / day (max ${FREE_MAX_FILE_MINUTES} min per file)`,
      'TTS: 5,000 characters / month',
      'YouTube channel preview (list + 1 free transcript)',
      'Models: OSS only',
    ],
    cta: 'Start free',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 25,
    annualPrice: 20,
    sttMinutes: 1000,
    ttsCharacters: 25000,
    premiumVoiceCap: 10,
    tagline: 'For creators & power users',
    features: [
      'STT: 1,000 minutes',
      'TTS: 25,000 characters',
      'Sync 1 YouTube channel + searchable library',
      'Repurpose episodes (blog, thread, show notes)',
      'Summaries & exports',
      'Fast processing queue',
      'Premium voices ≤ 10%',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'studio',
    name: 'Studio',
    monthlyPrice: 79,
    annualPrice: 63,
    sttMinutes: 3000,
    ttsCharacters: 90000,
    premiumVoiceCap: 25,
    tagline: 'For serious creators & small teams',
    features: [
      'STT: 3,000 minutes',
      'TTS: 90,000 characters',
      'Sync up to 3 channels + searchable library',
      'Turbo priority processing (included)',
      'Up to 3 users',
      'Batch uploads',
      'Premium voices ≤ 25%',
      'Export formats (SRT, DOCX, JSON)',
    ],
    cta: 'Get Started',
    popular: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    monthlyPrice: 149,
    annualPrice: 119,
    sttMinutes: 5000,
    ttsCharacters: 150000,
    premiumVoiceCap: 35,
    tagline: 'For teams, SMBs & API users',
    features: [
      'STT: 5,000 minutes',
      'TTS: 150,000 characters',
      'Sync up to 10 channels + searchable library',
      'Turbo priority processing (included)',
      'Team workspaces (up to 10 users)',
      'API access (rate-limited)',
      'Premium voices ≤ 35%',
      'Usage analytics',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 300,
    sttMinutes: Infinity,
    ttsCharacters: Infinity,
    tagline: 'Custom ($300+ / month)',
    features: [
      'Custom volumes',
      'BYO provider keys',
      'SLAs & compliance',
      'Dedicated routing & support',
    ],
    cta: 'Contact Sales',
    popular: false,
    isCustomPricing: true,
    customPricingLabel: 'Custom',
  },
];

